import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'tw:data-open:animate-in tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:bg-black/10 tw:duration-100 tw:supports-backdrop-filter:backdrop-blur-xs-no-thx tw:fixed tw:inset-0 tw:isolate tw:z-50',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'tw:bg-background tw:data-open:animate-in tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:data-closed:zoom-out-95 tw:data-open:zoom-in-95 tw:ring-foreground/10 tw:flex tw:max-w-[calc(100%-2rem)] tw:max-h-[calc(100vh-2rem)] tw:flex-col tw:gap-4 tw:overflow-hidden tw:rounded-xl tw:p-4 tw:text-sm tw:ring-1 tw:duration-100 tw:sm:max-w-sm tw:fixed tw:top-1/2 tw:left-1/2 tw:z-50 tw:w-full tw:-translate-x-1/2 tw:-translate-y-1/2',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              className="tw:absolute tw:top-2 tw:right-2"
              size="icon-sm"
            >
              <XIcon />
              <span className="tw:sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('tw:gap-2 tw:flex tw:flex-col tw:shrink-0', className)}
      {...props}
    />
  );
}

function DialogBody({
  className,
  children,
  gradientHeight = 24,
  ...props
}: React.ComponentProps<'div'> & {
  gradientHeight?: number;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [updateScrollState]);

  return (
    <div
      data-slot="dialog-body"
      className="tw:relative tw:flex-1 tw:min-h-0 tw:flex tw:flex-col"
      {...props}
    >
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className={cn(
          'tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:no-scrollbar tw:p-1 tw:-m-1',
          className,
        )}
      >
        {children}
      </div>
      {/* Top gradient */}
      <div
        style={{ height: gradientHeight }}
        className={cn(
          'tw:pointer-events-none tw:absolute tw:inset-x-0 tw:top-0 tw:bg-gradient-to-b tw:from-background tw:to-transparent tw:transition-opacity tw:duration-150',
          canScrollUp ? 'tw:opacity-100' : 'tw:opacity-0',
        )}
      />
      {/* Bottom gradient */}
      <div
        style={{ height: gradientHeight }}
        className={cn(
          'tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:bg-gradient-to-t tw:from-background tw:to-transparent tw:transition-opacity tw:duration-150',
          canScrollDown ? 'tw:opacity-100' : 'tw:opacity-0',
        )}
      />
    </div>
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'tw:-mx-4 tw:-mb-4 tw:gap-2 tw:rounded-b-xl tw:border-t tw:bg-muted/50 tw:p-4 tw:flex tw:flex-col-reverse tw:shrink-0 tw:sm:flex-row tw:sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('tw:text-lg tw:leading-none tw:font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'tw:text-muted-foreground tw:*:[a]:hover:text-foreground tw:text-sm tw:*:[a]:underline tw:*:[a]:underline-offset-3',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
